/**
 * Responsive chrome for the parent-packet input UI (all sites).
 * Desktop: two-pane (progress rail + form). Phone: single-column with collapsible rail.
 * Layout/container only — callers keep field, validation, and submit handlers.
 */
import React from 'react';
import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import {
  formatPacketSchoolYearLabel,
  formatPacketSiteLabel,
  HASC_BLUE,
  PACKET_FORM_CARD_MAX_WIDTH,
  PACKET_MAX_WIDTH,
} from './parentPacketBrand';

export default function ParentPacketLayout({
  isDesktop,
  siteName,
  siteSlug,
  childName,
  schoolYear,
  sessionType,
  currentIndex,
  totalForms,
  progressRail = null,
  mobileProgressRail = null,
  topExtras = null,
  children,
  onBack,
  backDisabled = false,
  onForward,
  forwardDisabled = false,
  forwardLabel,
  footerHint = null,
  draftSavedLabel = null,
}) {
  const formNum = currentIndex + 1;
  const progressPct = totalForms ? Math.round((formNum / totalForms) * 100) : 0;
  const titleChild = childName || 'your child';
  const yearLabel = formatPacketSchoolYearLabel(schoolYear, sessionType);
  const siteLabel = formatPacketSiteLabel(siteName, siteSlug);
  const phoneRail = mobileProgressRail || progressRail;

  const stickyHeader = (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: { xs: 2, md: 3 },
        py: 1.5,
        boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL || ''}/logo.png`}
          alt="HASC"
          sx={{
            height: { xs: 24, md: 28 },
            width: 'auto',
            flexShrink: 0,
            display: 'block',
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant={isDesktop ? 'subtitle1' : 'body2'}
            sx={{ fontWeight: 700, lineHeight: 1.3 }}
          >
            {siteLabel} · Parent packet for {titleChild}
          </Typography>
          {yearLabel ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 500, display: 'block', mt: 0.15 }}
            >
              {yearLabel}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mt: 1,
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}
        >
          {isDesktop ? `Form ${formNum} of ${totalForms}` : `${formNum} / ${totalForms}`}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{
            flex: 1,
            height: isDesktop ? 8 : 6,
            borderRadius: 4,
            bgcolor: HASC_BLUE.progressTrack,
            '& .MuiLinearProgress-bar': {
              bgcolor: HASC_BLUE.primary,
              borderRadius: 4,
            },
          }}
        />
      </Box>
    </Box>
  );

  const stickyFooter = (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 20,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        px: { xs: 2, md: 3 },
        py: 1.5,
        pb: { xs: 'calc(12px + env(safe-area-inset-bottom, 0px))', md: 1.5 },
        boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
      }}
    >
      {(footerHint || draftSavedLabel) && (
        <Box sx={{ mb: 1, textAlign: 'center' }}>
          {footerHint}
          {draftSavedLabel}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          alignItems: 'center',
          width: '100%',
          maxWidth: isDesktop ? PACKET_FORM_CARD_MAX_WIDTH : '100%',
          ml: isDesktop ? 'auto' : 0,
        }}
      >
        <Button
          variant="outlined"
          size="large"
          onClick={onBack}
          disabled={backDisabled}
          sx={{
            minWidth: { xs: 96, md: 120 },
            flexShrink: 0,
            py: { xs: 1.25, md: 1.1 },
            borderColor: HASC_BLUE.primary,
            color: HASC_BLUE.primary,
            '&:hover': {
              borderColor: HASC_BLUE.primaryHover,
              bgcolor: HASC_BLUE.highlight,
            },
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={onForward}
          disabled={forwardDisabled}
          sx={{
            flex: 1,
            py: { xs: 1.5, md: 1.25 },
            fontSize: { xs: '1.05rem', md: '1rem' },
            fontWeight: 600,
            bgcolor: HASC_BLUE.primary,
            '&:hover': { bgcolor: HASC_BLUE.primaryHover },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
          }}
        >
          {forwardLabel}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box
      data-packet-layout={isDesktop ? 'desktop' : 'phone'}
      data-woodmere-layout={isDesktop ? 'desktop' : 'phone'}
      sx={{
        maxWidth: PACKET_MAX_WIDTH,
        mx: 'auto',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f7fa',
      }}
    >
      {stickyHeader}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 0, md: 3 },
          px: { xs: 1.5, md: 3 },
          py: { xs: 2, md: 3 },
          alignItems: 'flex-start',
        }}
      >
        {isDesktop && progressRail && (
          <Box
            sx={{
              width: 280,
              flexShrink: 0,
              position: 'sticky',
              top: 88,
              alignSelf: 'flex-start',
              maxHeight: 'calc(100vh - 120px)',
              overflowY: 'auto',
            }}
          >
            {progressRail}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0, width: '100%', maxWidth: PACKET_FORM_CARD_MAX_WIDTH }}>
          {!isDesktop && phoneRail ? (
            <Box sx={{ mb: 2 }}>{phoneRail}</Box>
          ) : null}
          {topExtras}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3.5 },
              mb: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: '#fff',
            }}
          >
            {children}
          </Paper>
        </Box>
      </Box>
      {stickyFooter}
    </Box>
  );
}
